/**
 * Appointment status, Dentrix style (owner: "keep it how Dentrix does it: the
 * colour changes for the arrival status on the arrival indicator").
 *
 * Dentrix keeps ONE status per appointment covering both confirmation and
 * arrival (Office Manager > Practice Definitions > Appointment Status: up to
 * 20 per office, each with a colour). The Appointment Book draws it as a
 * coloured bar down the left side of the block. Velora mirrors that:
 *
 * - every office has a list of statuses, each with an abbreviation ("FIRM",
 *   "HERE", "SEATED", "LMM", "??????"), a description, a kind (what it means
 *   to Velora: confirmed, arrived, in chair, ...) and a colour;
 * - a PMS office's own codes are keyed `pms:<defid>` (Dentrix
 *   `admin.v_appt_status_codes.defid`, Open Dental DefNum), matched against
 *   the appointment's `confirmationPmsDef`;
 * - Velora's own statuses (a Velora-native office, or a check-in done in
 *   Velora that the PMS has not echoed yet) are keyed `velora:<kind>`.
 *
 * Colours are per office and editable (Settings > Appointment statuses); the
 * defaults below never use blue (owner rule).
 */
import { APPT_STATUS_ABBR_COLORS, APPT_STATUS_COLORS } from '@velora/tokens';
import { z } from 'zod';

export const APPT_STATUS_KINDS = [
  'unconfirmed',
  'confirmed',
  'sent',
  'message',
  'unreachable',
  'emergency',
  'here',
  'ready',
  'chair',
  'checkout',
  'other',
] as const;
export const ApptStatusKindSchema = z.enum(APPT_STATUS_KINDS);
export type ApptStatusKind = z.infer<typeof ApptStatusKindSchema>;

/** The kinds that mean the patient is in the office (they follow patient flow). */
export const ARRIVAL_KINDS: ReadonlySet<ApptStatusKind> = new Set([
  'here',
  'ready',
  'chair',
  'checkout',
]);

/** What each kind means, in plain words (Settings and the Legend). */
export const APPT_STATUS_KIND_LABEL: Readonly<Record<ApptStatusKind, string>> = {
  unconfirmed: 'Not confirmed',
  confirmed: 'Confirmed',
  sent: 'Confirmation sent',
  message: 'Left message',
  unreachable: 'Could not reach',
  emergency: 'Emergency',
  here: 'Arrived',
  ready: 'Ready for operatory',
  chair: 'Seated',
  checkout: 'At checkout',
  other: 'Other',
};

export const ApptStatusColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'A colour is #RRGGBB')
  .transform((s) => s.toLowerCase());

export const ApptStatusCodeSchema = z
  .string()
  // A PMS id may be a name with spaces (Dentrix codes such as "Left Message"
  // arrive by name); tabs and line breaks never.
  .regex(/^(pms|velora):[^\t\n\r]{1,64}$/, 'A status code is pms:<id> or velora:<kind>');

export const ApptStatusDefSchema = z.object({
  /** `pms:<defid>` (the office's PMS code) or `velora:<kind>`. */
  code: ApptStatusCodeSchema,
  /** Shown on the block and in the tooltip ("FIRM", "HERE", "??????"). */
  abbr: z.string().trim().min(1).max(12),
  description: z.string().trim().max(80),
  kind: ApptStatusKindSchema,
  color: ApptStatusColorSchema,
  /**
   * Where the code came from: `pms` the office's code table (sent by the
   * connector), `observed` a code seen on appointments whose name Velora
   * guessed, `velora` Velora's own status.
   */
  source: z.enum(['pms', 'observed', 'velora']),
  /** The office saved its own name or colour (false: a default). */
  custom: z.boolean(),
  /**
   * False: a PMS code no longer in the office's code table (full replace).
   * Kept so older appointments still show it. Absent: active.
   */
  active: z.boolean().optional(),
});
export type ApptStatusDef = z.infer<typeof ApptStatusDefSchema>;

export const ApptStatusListSchema = z.object({
  locationId: z.uuid(),
  statuses: z.array(ApptStatusDefSchema),
});
export type ApptStatusList = z.infer<typeof ApptStatusListSchema>;

export const ApptStatusPutRowSchema = z.object({
  code: ApptStatusCodeSchema,
  abbr: z.string().trim().min(1).max(12),
  description: z.string().trim().max(80),
  kind: ApptStatusKindSchema,
  /** Null: back to the default colour. */
  color: ApptStatusColorSchema.nullable(),
});
export type ApptStatusPutRow = z.infer<typeof ApptStatusPutRowSchema>;

export const ApptStatusPutSchema = z.object({
  locationId: z.uuid(),
  statuses: z.array(ApptStatusPutRowSchema).max(60),
});
export type ApptStatusPut = z.infer<typeof ApptStatusPutSchema>;

/* ------------------------------------------------------------------ defaults */

/**
 * Default colour per kind (packages/tokens/src/apptStatus.ts): one solid
 * colour each, like Dentrix, readable on both themes. No blue.
 */
export const KIND_COLOR: Readonly<Record<ApptStatusKind, string>> = APPT_STATUS_COLORS;

/** Abbreviation-specific defaults that differ from their kind's colour. */
const ABBR_COLOR: Readonly<Record<string, string>> = APPT_STATUS_ABBR_COLORS;

/** Velora's own statuses, Dentrix-style abbreviations. */
export const VELORA_STATUS_ABBR: Readonly<Record<ApptStatusKind, string>> = {
  unconfirmed: '??????',
  confirmed: 'FIRM',
  sent: 'SENT',
  message: 'LMM',
  unreachable: 'NOANS',
  emergency: 'ER',
  here: 'HERE',
  ready: 'READY',
  chair: 'SEATED',
  checkout: 'CHECKOUT',
  other: 'OTHER',
};

/** The kind a PMS abbreviation means (Dentrix defaults and Signature's codes). */
export function kindOfAbbr(abbr: string, description = ''): ApptStatusKind {
  const a = abbr.trim().toUpperCase();
  const d = description.trim().toLowerCase();
  // "??????" is Dentrix's not-yet-confirmed code (Appointment Set; Could Not Reach at Signature).
  if (/^\?+$/.test(a) || /appointment set|unconfirmed/.test(d)) return 'unconfirmed';
  if (/could not reach|no answer/.test(d)) return 'unreachable';
  if (a === 'HERE' || /arrived/.test(d)) return 'here';
  if (a === 'READY' || /ready for/.test(d)) return 'ready';
  if (a === 'SEATED' || a === 'SEAT' || a === 'CHAIR' || /seated|in chair/.test(d)) return 'chair';
  if (/^QC/.test(a) || /checkout|check out/.test(d)) return 'checkout';
  if (/^LM/.test(a) || /left message/.test(d)) return 'message';
  if (a === 'ER' || a === 'EMER' || /emergency/.test(d)) return 'emergency';
  // CONMO: McLaren's "confirmed (mobile)".
  if (a === 'FIRM' || /CONF/.test(a) || a === 'CONMO' || /confirm/.test(d)) return 'confirmed';
  return 'other';
}

/** The default colour of a status: its abbreviation's, else its kind's. */
export function defaultStatusColor(abbr: string, kind: ApptStatusKind): string {
  return ABBR_COLOR[abbr.trim().toUpperCase()] ?? KIND_COLOR[kind];
}

/** Velora's built-in status for a kind. */
export function veloraStatus(kind: ApptStatusKind): ApptStatusDef {
  const abbr = VELORA_STATUS_ABBR[kind];
  return {
    code: `velora:${kind}`,
    abbr,
    description: APPT_STATUS_KIND_LABEL[kind],
    kind,
    color: defaultStatusColor(abbr, kind),
    source: 'velora',
    custom: false,
  };
}

export const VELORA_STATUSES: readonly ApptStatusDef[] = APPT_STATUS_KINDS.map(veloraStatus);

/** Velora's confirmation vocabulary as a status kind. */
export function kindOfConfirmation(confirmation: string): ApptStatusKind {
  switch (confirmation) {
    case 'confirmed':
      return 'confirmed';
    case 'sent_text':
    case 'sent_email':
      return 'sent';
    case 'left_message':
    case 'waiting_callback':
      return 'message';
    case 'unreachable':
    case 'phone_busy':
      return 'unreachable';
    case 'other':
      return 'other';
    default:
      return 'unconfirmed';
  }
}

/* ------------------------------------------------------------------ resolve */

export interface StatusCardInput {
  readonly flowStatus: string;
  readonly confirmation: string;
  readonly confirmationPmsDef: string | null;
}

type ArrivalStep = 'here' | 'ready' | 'chair' | 'checkout';

/** Patient flow order: a later step means the visit has moved on. */
const STEP_ORDER: Readonly<Record<ArrivalStep, number>> = {
  here: 1,
  ready: 2,
  chair: 3,
  checkout: 4,
};

function isArrival(k: string): k is ArrivalStep {
  return ARRIVAL_KINDS.has(k as ApptStatusKind);
}

/**
 * True when the office's in-office code (HERE, SEATED, QC) is at least as far
 * along as the stored flow. The code is the PMS's word, so it wins a tie and
 * a lag of the feed (a SEATED code folded into flow `here`); a later stored
 * flow wins over a code that has not caught up yet: the PMS seat or dismiss
 * time read by the fast lane (`pms_seated_at`, flow `chair`) while the
 * code still says HERE, or a seat made in Velora not echoed by the PMS yet.
 */
function codeLeads(code: ArrivalStep, flow: string): boolean {
  return !isArrival(flow) || STEP_ORDER[code] >= STEP_ORDER[flow];
}

/**
 * The appointment's one status (the left bar). Done and broken visits are
 * resolved by the caller (grey done look, struck through).
 *
 * The office's PMS code comes first: the connector folds every "here /
 * seated / arrived" code into flow `here`, so a Dentrix SEATED visit arrives
 * with flow `here` and only its code (`confirmationPmsDef`) says SEATED.
 *
 * - A PMS code that means the patient is in the office (HERE, SEATED, QC):
 *   that code, unless the stored flow is further along (see codeLeads).
 * - The patient is in the office in Velora or further along than the code
 *   (a check-in made in Velora not echoed by the PMS yet, so the code still
 *   says FIRM; a PMS seat time while the code says HERE): Velora's status
 *   for that step.
 * - Otherwise the office's PMS code when the office has one for it.
 * - Otherwise Velora's status for the confirmation.
 *
 * {@link effectiveFlow} follows the same rule, so the bar and the counts agree.
 */
export function resolveApptStatus(
  c: StatusCardInput,
  byCode: ReadonlyMap<string, ApptStatusDef>,
): ApptStatusDef {
  const pms = c.confirmationPmsDef ? byCode.get(`pms:${c.confirmationPmsDef}`) : undefined;
  const flow = c.flowStatus;
  if (pms && isArrival(pms.kind) && codeLeads(pms.kind, flow)) return pms;
  if (isArrival(flow)) return byCode.get(`velora:${flow}`) ?? veloraStatus(flow);
  if (pms) return pms;
  const kind = kindOfConfirmation(c.confirmation);
  return byCode.get(`velora:${kind}`) ?? veloraStatus(kind);
}

/**
 * The flow step the counts, the flow chip and the Flow board use: the
 * office code's step when the code means the patient is in the office
 * (SEATED counts as Chair even though the feed says `here`), unless the
 * stored flow is further along; else the stored flow. Complete, broken and
 * not-yet-arrived visits keep their flow. Same rule as the left bar.
 */
export function effectiveFlow<F extends string>(
  c: StatusCardInput & { readonly flowStatus: F },
  byCode: ReadonlyMap<string, ApptStatusDef>,
): F | ArrivalStep {
  if (!isArrival(c.flowStatus)) return c.flowStatus;
  const pms = c.confirmationPmsDef ? byCode.get(`pms:${c.confirmationPmsDef}`) : undefined;
  if (pms && isArrival(pms.kind) && codeLeads(pms.kind, c.flowStatus)) return pms.kind;
  return c.flowStatus;
}

export function statusIndex(defs: readonly ApptStatusDef[]): Map<string, ApptStatusDef> {
  return new Map(defs.map((d) => [d.code, d]));
}

/** True for a blue-dominant colour (the owner's "no blue" rule, used by tests and Settings). */
export function isBlueColor(hex: string): boolean {
  const n = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!n) return false;
  const [r, g, b] = [n[1], n[2], n[3]].map((x) => parseInt(x as string, 16)) as [
    number,
    number,
    number,
  ];
  return b - Math.max(r, g) >= 12;
}
